<template>
  <div class="container-fluid">

    <!-- Cargando -->
    <div v-if="loading" class="text-center py-5">
      <div class="loader-box"><div class="loader-3"></div></div>
      <p>Cargando...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <h5>Error al cargar los datos</h5>
        <p>{{ error.message || 'Ocurrió un error inesperado' }}</p>
        <button class="btn btn-primary" @click="fetchInstrumento()">Reintentar</button>
      </div>
    </div>

    <!-- No encontrado -->
    <div v-else-if="!instrumento" class="text-center py-5">
      <div class="alert alert-warning">Instrumento no encontrado.</div>
      <button class="btn btn-outline-secondary" @click="$router.back()">
        <i class="fa fa-arrow-left me-1"></i> Regresar
      </button>
    </div>

    <div v-else>

      <!-- Hero banner -->
      <div class="row mt-3">
        <div class="col-12">
          <div class="card mb-0" style="overflow:hidden; border-radius: 15px 15px 0 0;">
            <img
              :src="instrumento.banner || instrumento.imagen || instrumento.thumbnail_url || instrumento.thumbnail || defaultImage"
              :alt="instrumento.title || instrumento.nombre"
              style="width:100%; height:150px; object-fit:cover; display:block;"
            />
          </div>
        </div>
      </div>

      <!-- Encabezado -->
      <div class="row">
        <div class="col-12">
          <div class="card" style="border-radius: 0 0 15px 15px;">
            <div class="card-body d-flex align-items-center gap-4 flex-wrap">
              <img
                v-if="instrumento.logo || instrumento.logo_url"
                :src="instrumento.logo || instrumento.logo_url"
                :alt="(instrumento.title || instrumento.nombre) + ' logo'"
                style="height:80px; width:auto; object-fit:contain; border-radius:8px; border:1px solid #e0e0e0; padding:6px; background:#fff;"
              />
              <div class="flex-grow-1">
                <h4 class="mb-1" style="color:#182e50;">{{ instrumento.title || instrumento.nombre }}</h4>
                <span
                  v-for="(t, i) in nombresDeArray([instrumento.tipo || instrumento.type].flat())"
                  :key="'tipo-'+i"
                  class="badge me-1"
                  style="background-color:#182e50;"
                >{{ t }}</span>
                <span
                  v-for="(st, i) in nombresDeArray([instrumento.subtipo || instrumento.subtype].flat())"
                  :key="'sub-'+i"
                  class="badge bg-success me-1"
                >{{ st }}</span>
                <span v-for="(po, i) in publicoObjetivo" :key="'po-'+i" class="badge bg-success me-1">{{ po }}</span>
              </div>
              <div class="d-flex gap-2 flex-wrap">
                <a v-if="instrumento.sitio_web || instrumento.web || instrumento.website || instrumento.url" :href="instrumento.sitio_web || instrumento.web || instrumento.website || instrumento.url" target="_blank" class="btn btn-outline-primary btn-sm">
                  <i class="fa fa-globe me-1"></i> Sitio web
                </a>
                <a v-if="instrumento.email || instrumento.correo || instrumento.correo_electronico" :href="'mailto:' + (instrumento.email || instrumento.correo || instrumento.correo_electronico)" class="btn btn-primary btn-sm">
                  <i class="fa fa-envelope me-1"></i> Contactar
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row starter-main">

        <!-- Columna principal -->
        <div class="col-lg-8 col-12">

          <!-- Descripción -->
          <div v-if="instrumento.descripcion || instrumento.intro || instrumento.description || instrumento.body" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-info-circle me-2"></i>Acerca del instrumento</h5>
            </div>
            <div class="card-body">
              <div class="rich-text mb-0" v-html="instrumento.descripcion || instrumento.intro || instrumento.description || instrumento.body"></div>
            </div>
          </div>

          <!-- Objetivos -->
          <div v-if="objetivos.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-bullseye me-2"></i>Objetivos</h5>
            </div>
            <div class="card-body">
              <ol class="ps-3 mb-0">
                <li v-for="(obj, i) in objetivos" :key="i" class="mb-2">{{ obj }}</li>
              </ol>
            </div>
          </div>

          <!-- Beneficios -->
          <div v-if="beneficios.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-star me-2"></i>Beneficios</h5>
            </div>
            <div class="card-body">
              <ul class="list-unstyled mb-0">
                <li v-for="(ben, i) in beneficios" :key="i" class="mb-2 d-flex align-items-start">
                  <i class="fa fa-check-circle me-2 mt-1" style="color:#182e50; flex-shrink:0;"></i>
                  <span>{{ ben }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Requisitos -->
          <div v-if="requisitos.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-clipboard me-2"></i>Requisitos</h5>
            </div>
            <div class="card-body">
              <ol class="mb-0 ps-3">
                <li v-for="(req, i) in requisitos" :key="i" class="mb-2">{{ req }}</li>
              </ol>
            </div>
          </div>

          <!-- Notas adicionales -->
          <div v-if="instrumento.notas_adicionales" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-sticky-note-o me-2"></i>Notas adicionales</h5>
            </div>
            <div class="card-body">
              <div class="rich-text" v-html="instrumento.notas_adicionales"></div>
            </div>
          </div>

        </div>

        <!-- Columna lateral -->
        <div class="col-lg-4 col-12">

          <!-- Objetivo -->
          <div v-if="instrumento.objetivo || instrumento.modalidad || instrumento.monto" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-bullseye me-2"></i>Objetivo</h5>
            </div>
            <div class="card-body">
              <div v-if="instrumento.objetivo" class="mb-3">
                <p class="fw-bold mb-1" style="color:#182e50;">Objetivo</p>
                <div class="rich-text" v-html="instrumento.objetivo"></div>
              </div>
              <div v-if="instrumento.modalidad" class="mb-2">
                <p class="fw-bold mb-1" style="color:#182e50;"><i class="fa fa-desktop me-2"></i>Modalidad</p>
                <p class="mb-0">{{ stripHtml(instrumento.modalidad) }}</p>
              </div>
              <div v-if="instrumento.monto" class="mb-0">
                <p class="fw-bold mb-1" style="color:#182e50;"><i class="fa fa-money me-2"></i>Monto</p>
                <p class="mb-0">{{ instrumento.monto }}</p>
              </div>
            </div>
          </div>

          <!-- Condiciones -->
          <div v-if="instrumento.periodicidad || instrumento.fecha_inicio || instrumento.fecha_fin || instrumento.costo" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-file-text-o me-2"></i>Condiciones</h5>
            </div>
            <div class="card-body">
              <ul class="list-unstyled mb-0">
                <li v-if="instrumento.periodicidad" class="mb-2">
                  <i class="fa fa-refresh me-2" style="color:#182e50;"></i>
                  <strong class="me-1">Periodicidad:</strong>{{ instrumento.periodicidad }}
                </li>
                <li v-if="instrumento.fecha_inicio" class="mb-2">
                  <i class="fa fa-calendar me-2" style="color:#182e50;"></i>
                  <strong class="me-1">Inicio:</strong>{{ instrumento.fecha_inicio }}
                </li>
                <li v-if="instrumento.fecha_fin" class="mb-2">
                  <i class="fa fa-calendar-check-o me-2" style="color:#182e50;"></i>
                  <strong class="me-1">Fin:</strong>{{ instrumento.fecha_fin }}
                </li>
                <li v-if="instrumento.costo" class="mb-0">
                  <i class="fa fa-tag me-2" style="color:#182e50;"></i>
                  <strong class="me-1">Costo:</strong>{{ instrumento.costo }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Recursos -->
          <div v-if="instrumento.documento_adjunto || instrumento.enlace_web || instrumento.contacto_email" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-paperclip me-2"></i>Recursos</h5>
            </div>
            <div class="card-body">
              <a v-if="instrumento.documento_adjunto" :href="instrumento.documento_adjunto" target="_blank" rel="noopener noreferrer" class="btn btn-outline-primary btn-sm w-100 mb-2">
                <i class="fa fa-download me-2"></i>Descargar documento adjunto
              </a>
              <a v-if="instrumento.enlace_web" :href="instrumento.enlace_web" target="_blank" rel="noopener noreferrer" class="btn btn-outline-secondary btn-sm w-100 mb-2">
                <i class="fa fa-globe me-2"></i>Visitar sitio web
              </a>
              <a v-if="instrumento.contacto_email" :href="'mailto:' + instrumento.contacto_email" class="btn btn-outline-secondary btn-sm w-100">
                <i class="fa fa-envelope me-2"></i>{{ instrumento.contacto_email }}
              </a>
            </div>
          </div>

          <!-- Contacto -->
          <div v-if="instrumento.email || instrumento.correo || instrumento.correo_electronico || instrumento.telefono || instrumento.phone || instrumento.direccion || instrumento.address || instrumento.sitio_web || instrumento.web || instrumento.website" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-address-card me-2"></i>Contacto</h5>
            </div>
            <div class="card-body">
              <ul class="list-unstyled mb-0">
                <li v-if="instrumento.email || instrumento.correo || instrumento.correo_electronico" class="mb-2">
                  <i class="fa fa-envelope me-2" style="color:#182e50;"></i>
                  <a :href="'mailto:' + (instrumento.email || instrumento.correo || instrumento.correo_electronico)">{{ instrumento.email || instrumento.correo || instrumento.correo_electronico }}</a>
                </li>
                <li v-if="instrumento.telefono || instrumento.phone" class="mb-2">
                  <i class="fa fa-phone me-2" style="color:#182e50;"></i>{{ instrumento.telefono || instrumento.phone }}
                </li>
                <li v-if="instrumento.direccion || instrumento.address" class="mb-2">
                  <i class="fa fa-map-marker me-2" style="color:#182e50;"></i>{{ instrumento.direccion || instrumento.address }}
                </li>
                <li v-if="instrumento.sitio_web || instrumento.web || instrumento.website || instrumento.url" class="mb-0">
                  <i class="fa fa-globe me-2" style="color:#182e50;"></i>
                  <a :href="instrumento.sitio_web || instrumento.web || instrumento.website || instrumento.url" target="_blank">{{ instrumento.sitio_web || instrumento.web || instrumento.website || instrumento.url }}</a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Indicadores -->
          <div v-if="indicadores.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-bar-chart me-2"></i>Indicadores clave</h5>
            </div>
            <div class="card-body">
              <div class="mb-3" v-for="(ind, i) in indicadores" :key="i">
                <div class="d-flex justify-content-between mb-1">
                  <small>{{ ind.label }}</small>
                  <small class="fw-bold">{{ ind.valor || ind.value }}</small>
                </div>
                <div class="progress" style="height:8px;">
                  <div class="progress-bar" :style="'width:' + (ind.pct || ind.percent || 0) + '%; background-color:#182e50;'"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sectores / Estado / Escala / Etiquetas -->
          <div v-if="sectoresNombres.length || estadosNombres.length || escalasNombres.length || etiquetas.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-tags me-2"></i>Clasificación</h5>
            </div>
            <div class="card-body">
              <div v-if="sectoresNombres.length" class="mb-2">
                <small class="text-muted d-block mb-1">Sector</small>
                <span class="badge bg-secondary me-1 mb-1" v-for="(s, i) in sectoresNombres" :key="'sec-'+i">{{ s }}</span>
              </div>
              <div v-if="estadosNombres.length" class="mb-2">
                <small class="text-muted d-block mb-1">Estado</small>
                <span class="badge me-1 mb-1" style="background-color:#182e50;" v-for="(e, i) in estadosNombres" :key="'est-'+i">{{ e }}</span>
              </div>
              <div v-if="escalasNombres.length" class="mb-2">
                <small class="text-muted d-block mb-1">Escala</small>
                <span class="badge bg-success me-1 mb-1" v-for="(e, i) in escalasNombres" :key="'esc-'+i">{{ e }}</span>
              </div>
              <div v-if="etiquetas.length">
                <small class="text-muted d-block mb-1">Temas</small>
                <span class="badge bg-secondary me-1 mb-1" v-for="(tag, i) in etiquetas" :key="'tag-'+i">{{ tag }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Botón regresar -->
      <div class="row mb-4">
        <div class="col-12">
          <button class="btn btn-outline-secondary" @click="$router.back()">
            <i class="fa fa-arrow-left me-1"></i> Regresar a instrumentos
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/axios";
import defaultImageSrc from "@/assets/images/verde/ecobanner.webp";

export default {
  name: "DetalleInstrumento",
  data() {
    return {
      instrumento: null,
      loading: true,
      error: null,
    };
  },
  computed: {
    defaultImage() {
      return defaultImageSrc;
    },
    objetivos() {
      const raw = this.instrumento?.objetivos || this.instrumento?.objectives || "";
      if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
      if (typeof raw === "string") return this.parseRichTextItems(raw);
      return [];
    },
    beneficios() {
      const raw = this.instrumento?.beneficios || this.instrumento?.benefits || "";
      if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
      if (typeof raw === "string") return this.parseRichTextItems(raw);
      return [];
    },
    requisitos() {
      const raw = this.instrumento?.requisitos || this.instrumento?.requirements || "";
      if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
      if (typeof raw === "string") return this.parseRichTextItems(raw);
      return [];
    },
    indicadores() {
      return this.instrumento?.indicadores || this.instrumento?.indicators || [];
    },
    etiquetas() {
      const raw = this.instrumento?.etiquetas || this.instrumento?.tags || [];
      const base = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(",").map((s) => s.trim()) : [];
      return base.map((t) => (typeof t === "object" ? t.nombre : t)).filter(Boolean);
    },
    publicoObjetivo() {
      const raw = this.instrumento?.publico_objetivo;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.map((x) => (typeof x === "object" ? x.nombre : x)).filter(Boolean);
      return this.parseRichTextItems(raw);
    },
    sectoresNombres() {
      return this.nombresDeArray(this.instrumento?.sector);
    },
    estadosNombres() {
      return this.nombresDeArray(this.instrumento?.estado);
    },
    escalasNombres() {
      return this.nombresDeArray(this.instrumento?.escala);
    },
  },
  created() {
    this.fetchInstrumento();
  },
  methods: {
    nombresDeArray(arr) {
      if (!Array.isArray(arr)) return [];
      return arr.map((item) => (typeof item === "object" ? item.nombre : item)).filter(Boolean);
    },
    stripHtml(html) {
      if (!html) return "";
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    },
    parseRichTextItems(html) {      if (!html) return [];
      const div = document.createElement("div");
      div.innerHTML = html;
      const lis = Array.from(div.querySelectorAll("li"));
      if (lis.length) return lis.map((el) => el.textContent.trim()).filter(Boolean);
      const ps = Array.from(div.querySelectorAll("p"));
      if (ps.length) return ps.map((el) => el.textContent.trim()).filter(Boolean);
      return div.textContent.split(",").map((s) => s.trim()).filter(Boolean);
    },
    async fetchInstrumento() {
      this.loading = true;
      this.error = null;
      try {
        const id = this.$route.params.id;
        const { data } = await api.get(`instrumentos/${id}/`);
        this.instrumento = data;
      } catch (err) {
        this.error = err;
        console.error("Error al cargar instrumento:", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.rich-text p { margin-bottom: 0.75rem; }
.rich-text p:last-child { margin-bottom: 0; }
.rich-text ul, .rich-text ol { padding-left: 1.25rem; margin-bottom: 0.75rem; }
.rich-text li { margin-bottom: 0.25rem; }
.rich-text h1, .rich-text h2, .rich-text h3, .rich-text h4 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #182e50;
}
.rich-text a { color: #09377c; }
.rich-text img { max-width: 100%; height: auto; border-radius: 6px; margin: 0.5rem 0; }
</style>
